import { useState } from "react";
import { Download, ArrowRight } from "lucide-react";
import { toast } from "sonner";
import axios from "axios";
import Seo from "@/components/common/Seo";
import PageHeader from "@/components/common/PageHeader";
import { Reveal } from "@/components/common/Reveal";
import CTASection from "@/components/common/CTASection";
import { RESOURCES } from "@/data/content";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Resources() {
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await axios.post(`${API}/subscribe`, { email });
      toast.success("Thank you. Your briefings are on the way.");
      setEmail("");
    } catch {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <Seo
        title="Resources"
        description="Executive guides, toolkits and playbooks on technology strategy, AI readiness, ERP decisions and fractional CIO leadership."
        path="/resources"
        keywords={["Executive Technology Advisor", "AI Strategy Consultant", "Technology Strategy Consultant", "Enterprise Technology Leadership", "Fractional CIO"]}
      />
      <PageHeader
        overline="Executive briefings"
        titleLines={["Tools built for", "the boardroom."]}
        subtitle="Concise, practical frameworks distilled from real engagements, the kind of thinking you can take straight into your next leadership meeting."
        testId="resources-header"
      />
      <section data-testid="resources-grid" className="bg-white py-24 md:py-32">
        <div className="mx-auto max-w-[1400px] px-6 md:px-12">
          <div className="grid gap-6 md:grid-cols-2">
            {RESOURCES.map((r, i) => (
              <Reveal key={r.title} delay={(i % 2) * 0.08}>
                <div data-testid={`resource-${i}`} className="group flex items-start gap-6 rounded-[1.5rem] border border-slate-200 bg-surface p-8 transition-colors hover:border-royal">
                  <div className="flex h-14 w-14 flex-shrink-0 items-center justify-center rounded-2xl bg-navy/5 text-navy transition-colors group-hover:bg-navy group-hover:text-white">
                    <Download className="h-6 w-6" strokeWidth={1.5} />
                  </div>
                  <div>
                    <span className="font-body text-xs font-bold uppercase tracking-[0.2em] text-royal">{r.type}</span>
                    <h3 className="mt-2 font-heading text-2xl font-bold leading-snug tracking-tight text-navy">{r.title}</h3>
                    <p className="mt-2 font-body leading-relaxed text-slate-600">{r.desc}</p>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>

          <Reveal>
            <div className="mt-16 grid gap-8 rounded-[1.75rem] bg-navy p-8 text-white md:grid-cols-2 md:p-12">
              <div>
                <h2 className="font-heading text-3xl font-light leading-tight tracking-tight md:text-4xl">
                  Request the full <span className="font-black text-sky">briefing library</span>.
                </h2>
                <p className="mt-4 font-body text-lg leading-relaxed text-slate-300">
                  Leave your email and we&rsquo;ll send the complete set of executive briefings, along with new frameworks as they&rsquo;re published.
                </p>
              </div>
              <form onSubmit={submit} className="flex flex-col justify-center gap-3">
                <input
                  data-testid="resources-email-input"
                  type="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="Your email address"
                  className="w-full rounded-full border border-white/15 bg-white/5 px-6 py-4 font-body text-white placeholder:text-slate-400 focus:border-sky focus:outline-none focus:ring-2 focus:ring-sky/30"
                />
                <button
                  data-testid="resources-submit"
                  disabled={busy}
                  className="group inline-flex items-center justify-center gap-2 rounded-full bg-royal px-8 py-4 font-body font-semibold text-white transition-colors hover:bg-sky hover:text-navy disabled:opacity-60"
                >
                  {busy ? "Sending…" : "Request the library"}
                  <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
                </button>
              </form>
            </div>
          </Reveal>
        </div>
      </section>
      <CTASection
        overline="From framework to action"
        heading={["Put these ideas to work", "in your business."]}
        primary={{ label: "Book Executive Strategy Session", to: "/book" }}
        secondary={{ label: "Request Technology Assessment", to: "/book" }}
      />
    </>
  );
}
